<template>
    <div>
        <mini-button class="ml-1" variant="light"
                     @btnClick="openFormModal">
            Form
        </mini-button>

        <re-b-modal ref="formModal" header="Form"
                    fit-content>
            <mini-table read-only
                        effect-origin-row
                        row-key="key"
                        :items="localItems"
                        :fields="finallyFormFields"
                        custom-columns="type_extend">
                <template #custom(type_extend)="{row}">
                    <template v-if="row.type === 'select'">
                        <select-config :item="row"></select-config>
                    </template>
                    <template v-else-if="row.type === 'entity'">
                        <entity-from-config :item="row"></entity-from-config>
                    </template>
                </template>
                <template #rightHeader>
                    <mini-button confirm-text="确定Reset吗？" @btnClick="reset">
                        Reset
                    </mini-button>
                    <b-button variant="outline-primary" @click="newEditForm">
                        <i class="fa-solid fa-plus"></i>
                        EditForm
                    </b-button>
                    <b-button variant="outline-primary" @click="openEditFormEdit">
                        <i class="fa-solid fa-caret-right"></i>
                    </b-button>
                </template>
            </mini-table>
        </re-b-modal>

        <re-b-modal header="Edit Forms" ref="editForms" no-ok-button>
            <template v-for="(item,index) of editFormNames">
                <common-e-d-input :key="index" :value="item" class="mb-1 ml-2"
                                  @save="$set(editFormNames, index, $event)"
                                  @delete="editFormNames.splice(index,1)"></common-e-d-input>
            </template>
        </re-b-modal>
    </div>
</template>

<script>
import MiniButton from "@/components/common/buttons/MiniButton.vue";
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";
import MiniTable from "@/components/common/mini-table/MiniTable.vue";
import SelectConfig from "@/components/jsc/qc-cell/stuff/form/SelectConfig.vue";
import CommonEDInput from "@/components/common/inputs/CommonEDInput.vue";
import EntityFromConfig from "@/components/jsc/qc-cell/stuff/form/EntityFromConfig.vue";

export default {
    name: "FormConfig",
    mixins: [emCommonUtilMixin],
    components: {EntityFromConfig, CommonEDInput, SelectConfig, MiniTable, ReBModal, MiniButton},
    props: {
        items: Array,
    },
    data() {
        return {
            localItemMap: {},
            formConfig: {},
            formFields: [
                {key: 'key', label: 'Key'},
                {key: 'visible', label: '可见', isInput: true, type: 'checkbox'},
                {
                    key: 'type',
                    label: '类型',
                    isInput: true,
                    type: 'select',
                    options: row => this.getOptions(row.originalType)
                },
            ],
            formAddition: {
                'select': ['options', 'optionsFromDict'],
                'entity': ['entityFrom', 'entityFromByKey'],
                '*': ['originalType']
            },

            originalTypeMap: {},
            editFormNames: [],
            editFormFields: {},
        }
    },
    computed: {
        localItems() {
            return Object.values(this.localItemMap);
        },
        finallyFormFields() {
            let tempList = [];
            this.pushAll(this.formFields, tempList);
            tempList.push({
                key: 'type_extend',
                label: '配置'
            });
            for (let item of this.editFormNames) {
                tempList.push({
                    key: item,
                    label: 'EF-' + item,
                    type: 'checkbox',
                    isInput: true,
                    defaultValue: row => {
                        return row.isTargetFrom;
                    },
                    disableWhen: row => {
                        return !row.isTargetFrom;
                    }
                })
            }
            return tempList;
        },
        entityConfigs() {
            return this.$store.state.queryConfigStore.entityConfigs;
        },
    },
    watch: {
        items: {
            handler() {
                this.$nextTick(() => this.handleItems());
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        openFormModal() {
            this.$refs.formModal.showModal();
        },
        handleItems(clear = false) {
            let tempMap = {};
            for (let item of this.items) {
                let originType = null;
                let key = item.alias || item.key;
                if (this.entityConfigs) {
                    if (item.entity in this.entityConfigs) {
                        for (let f of this.entityConfigs[item.entity].fields) {
                            if (f.key === item.key) {
                                this.$set(this.originalTypeMap, key, f.type);
                                originType = f.type;
                                break;
                            }
                        }
                    }
                }
                let temp;
                if (!clear && key in this.localItemMap) {
                    temp = this.localItemMap[key];
                } else {
                    temp = {
                        key: key,
                        visible: true,
                    }
                    let fc = this.formConfig[key];
                    if (!clear && fc) {
                        this.copyObj2Obj(fc, temp, true);
                    }
                }
                let options = this.getOptions(originType);
                let got = false;
                if (temp.type) {
                    for (let op of options) {
                        if (temp.type === op.value) {
                            got = true;
                            break;
                        }
                    }
                }
                if (!got) {
                    temp.type = originType === 'Date' ? 'datetime' : 'input';
                }
                if (temp.key in this.originalTypeMap) {
                    temp.originalType = this.originalTypeMap[temp.key];
                }
                temp.isTargetFrom = item.target === this.$store.state.queryConfigStore.currentFrom.target;
                for (let name of this.editFormNames) {
                    let fieldSet = this.editFormFields[name];
                    if (fieldSet) {
                        temp[name] = fieldSet.has(temp.key);
                    }
                }
                tempMap[key] = temp;
            }
            this.localItemMap = tempMap;
        },
        unpackConfig(config) {
            if (config) {
                let temp = {};
                let form = config.form;
                if (form) {
                    for (let item of form) {
                        temp[item.key] = item;
                    }
                }
                this.formConfig = temp;
                let tempList = [], tempMap = {};
                if (this.hasCode(config.editForms)) {
                    for (let name of Object.keys(config.editForms)) {
                        tempList.push(name);
                        let set = new Set();
                        for (let f of config.editForms[name]) {
                            set.add(f);
                        }
                        tempMap[name] = set;
                    }
                }
                this.editFormNames = tempList;
                this.editFormFields = tempMap;
            } else {
                this.formConfig = {};
                this.originalTypeMap = {};
                this.editFormNames = [];
                this.editFormFields = {};
            }
            this.localItemMap = {};
        },
        gatherConfig() {
            let result = [];
            for (let item of this.localItems) {
                let t = {};
                for (let f of this.formFields) {
                    t[f.key] = item[f.key];
                }
                let type = item.type;
                this.addValueByFormAddition(type, item, t);
                this.addValueByFormAddition('*', item, t);
                result.push(t);
            }
            let editForms = {};
            for (let name of this.editFormNames) {
                let tempList = [];
                for (let item of this.localItems) {
                    if (item[name]) {
                        tempList.push(item.key);
                    }
                }
                editForms[name] = tempList;
            }
            return {
                form: result,
                editForms: editForms
            }
        },
        addValueByFormAddition(type, source, target) {
            if (type in this.formAddition) {
                for (let f of this.formAddition[type]) {
                    let v = source[f];
                    if (v !== undefined) {
                        target[f] = v;
                    }
                }
            }
        },
        getOptions(originalType) {
            if (originalType === 'Date') {
                return [
                    {value: 'date', text: 'Date'},
                    {value: 'datetime', text: 'Datetime'},
                ];
            } else {
                return [
                    {value: 'input', text: 'Input'},
                    {value: 'entity', text: 'From View'},
                    {value: 'select', text: 'Select'},
                    {value: 'radio-select', text: 'Radio'},
                    {value: 'textarea', text: 'Textarea'},
                    {value: 'image', text: 'Image'},
                ];
            }
        },
        reset() {
            this.handleItems(true);
        },
        newEditForm() {
            if (this.editFormNames.length === 0) {
                this.editFormNames.push('default');
            } else {
                this.editFormNames.push(this.genNonDuplicateID(4));
            }
        },
        openEditFormEdit() {
            if (this.editFormNames.length === 0) {
                this.newEditForm();
            }
            this.$refs.editForms.showModal();
        },
    }
}
</script>

<style scoped>

</style>
